import React, { useRef, useEffect } from 'react';

// Mirrors the kpis / kpi_definitions table CHECK constraint. Keep these
// in sync if the constraint changes (see baseline migration).
const KPI_TYPES = ['Sales', 'Operations', 'Customer Satisfaction', 'Engagement', 'Custom'];

export function KPISystemDefinitionsTab({
  definitions,
  showAddDefinitionForm,
  setShowAddDefinitionForm,
  newDefinition,
  setNewDefinition,
  previewedKpiCode,
  editingDefinition,
  setEditingDefinition,
  onAddDefinition,
  onUpdateDefinition,
  onDeleteDefinition,
  onCancelAdd,
  loading,
  error,
}) {
  const addFormRef = useRef(null);
  const editFormRef = useRef(null);
  const editingId = editingDefinition?.id;

  // The add/edit forms render at the bottom of this scrollable tab. Bring the
  // freshly-opened one into view so clicking Add/Edit isn't a silent no-op when
  // the definitions list is long enough to push the form below the scroll fold.
  useEffect(() => {
    if (editingId) editFormRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [editingId]);

  useEffect(() => {
    if (showAddDefinitionForm) addFormRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [showAddDefinitionForm]);

  return (
    <div className="definitions-tab">
      <button
        className="btn-primary"
        onClick={() => setShowAddDefinitionForm(true)}
        style={{ marginBottom: '20px' }}
        disabled={loading}
      >
        + Add KPI Definition
      </button>

      {error && <div className="error-message" role="alert">{error}</div>}

      <div className="definitions-list">
        {definitions.map((def) => (
          <div key={def.id} className="definition-item">
            <div className="definition-header">
              <div>
                <strong>{def.kpi_name}</strong>
                {def.description && <p className="definition-description">{def.description}</p>}
              </div>
              <div className="definition-actions">
                <button
                  className="btn-edit"
                  onClick={() => setEditingDefinition(def)}
                  disabled={loading}
                >
                  Edit
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => onDeleteDefinition(def.id)}
                  disabled={loading}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showAddDefinitionForm && (
        <div className="definition-form" ref={addFormRef}>
          <h4>Add KPI Definition</h4>
          <label htmlFor="add-def-name">KPI Name *</label>
          <input
            id="add-def-name"
            type="text"
            placeholder="e.g. Off Trade"
            value={newDefinition.name}
            onChange={(e) => setNewDefinition({ ...newDefinition, name: e.target.value })}
            aria-describedby="add-def-code-preview"
            aria-invalid={(!!newDefinition.name && !previewedKpiCode) || undefined}
            maxLength={100}
          />
          {newDefinition.name && (
            <small id="add-def-code-preview" className="kpi-code-preview">
              Code: <code>{previewedKpiCode || '(empty — add letters or numbers)'}</code>
            </small>
          )}
          <label htmlFor="add-def-type">KPI Type *</label>
          <select
            id="add-def-type"
            value={newDefinition.kpi_type}
            onChange={(e) => setNewDefinition({ ...newDefinition, kpi_type: e.target.value })}
          >
            <option value="">Select KPI Type…</option>
            {KPI_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <label htmlFor="add-def-description">Description</label>
          <textarea
            id="add-def-description"
            placeholder="Optional"
            value={newDefinition.description}
            onChange={(e) => setNewDefinition({ ...newDefinition, description: e.target.value })}
            maxLength={500}
          />
          <button
            className="btn-primary"
            onClick={onAddDefinition}
            disabled={loading || !newDefinition.name.trim() || !previewedKpiCode || !newDefinition.kpi_type}
          >
            Create Definition
          </button>
          <button
            className="btn-secondary"
            onClick={onCancelAdd}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      )}

      {editingDefinition && (
        <div className="definition-form" ref={editFormRef}>
          <h4>Edit KPI Definition</h4>
          <label htmlFor="edit-def-name">KPI Name *</label>
          <input
            id="edit-def-name"
            type="text"
            value={editingDefinition.kpi_name}
            onChange={(e) => setEditingDefinition({ ...editingDefinition, kpi_name: e.target.value })}
            maxLength={100}
          />
          <label htmlFor="edit-def-description">Description</label>
          <textarea
            id="edit-def-description"
            placeholder="Optional"
            value={editingDefinition.description || ''}
            onChange={(e) => setEditingDefinition({ ...editingDefinition, description: e.target.value })}
            maxLength={500}
          />
          <button
            className="btn-primary"
            onClick={() => onUpdateDefinition(editingDefinition.id)}
            disabled={loading || !editingDefinition.kpi_name.trim()}
          >
            Save Changes
          </button>
          <button
            className="btn-secondary"
            onClick={() => setEditingDefinition(null)}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
